import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, ScrollView, Dimensions} from 'react-native';
import {Card, Text, Menu, Button, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {BarChart} from 'react-native-chart-kit';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const screenWidth = Dimensions.get('window').width;

const chartConfig = {
  backgroundColor: colors.surface,
  backgroundGradientFrom: colors.surface,
  backgroundGradientTo: colors.surface,
  decimalPlaces: 0,
  color: (opacity = 1) => `rgba(27, 94, 32, ${opacity})`,
  labelColor: () => colors.textSecondary,
  barPercentage: 0.5,
  propsForLabels: {fontSize: 10},
};

const ReportMonthlyTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {monthlyData, isLoading, fetchMonthlyData} = useReportStore();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [menuVisible, setMenuVisible] = useState(false);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const loadData = useCallback(() => {
    fetchMonthlyData(year, orgUnitId);
  }, [year, orgUnitId, fetchMonthlyData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const buildChartData = (entries) => {
    if (!entries || entries.length === 0) {
      return {labels: MONTH_LABELS, datasets: [{data: new Array(12).fill(0)}]};
    }
    return {
      labels: MONTH_LABELS,
      datasets: [{data: entries.map(e => Number(e.value) || 0)}],
    };
  };

  const renderChart = (title, entries, barColor) => {
    const data = buildChartData(entries);
    const config = {
      ...chartConfig,
      color: (opacity = 1) => barColor.replace('1)', `${opacity})`),
    };
    return (
      <Card style={styles.chartCard} mode="elevated">
        <Card.Content>
          <Text style={styles.chartTitle}>{title}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <BarChart
              data={data}
              width={Math.max(screenWidth - spacing.md * 4, 350)}
              height={200}
              chartConfig={config}
              fromZero
              showValuesOnTopOfBars
              withInnerLines={false}
              style={styles.chart}
            />
          </ScrollView>
        </Card.Content>
      </Card>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.yearSelector}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <Button
              mode="outlined"
              onPress={() => setMenuVisible(true)}
              icon="calendar"
              contentStyle={styles.yearButtonContent}>
              {year}
            </Button>
          }>
          {years.map(y => (
            <Menu.Item
              key={y}
              onPress={() => { setYear(y); setMenuVisible(false); }}
              title={String(y)}
            />
          ))}
        </Menu>
      </View>

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : monthlyData ? (
        <>
          {renderChart(t('report.monthlySales'), monthlyData.monthlySales, 'rgba(27, 94, 32, 1)')}
          {renderChart(t('report.monthlyPayments'), monthlyData.monthlyPayments, 'rgba(25, 118, 210, 1)')}
          {renderChart(t('report.monthlyOrders'), monthlyData.monthlyOrders, 'rgba(255, 111, 0, 1)')}
        </>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  contentContainer: {padding: spacing.md},
  yearSelector: {marginBottom: spacing.md, alignItems: 'flex-start'},
  yearButtonContent: {flexDirection: 'row-reverse'},
  loader: {marginTop: spacing.xl},
  chartCard: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  chart: {
    borderRadius: 8,
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportMonthlyTab;
